<script setup lang="ts">
import { computed, h, nextTick, ref, render, watch, watchEffect } from 'vue'
import { useStore } from 'vuex'
import { unrefElement } from '@vueuse/core'
import bbox from '@turf/bbox'
import { useRouter, useRoute } from 'vue-router'

import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import { Eye, EyeClosed, ScanEye, Trash2, Globe, Package, Radar, Folders, Image, Ellipsis, GripVertical, ArrowUp, ArrowDown, Pencil, Check, Satellite } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import { LayerItemMapLayer } from '@/components/layers'
import { Spinner } from '@/components/ui/spinner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

const router = useRouter()
const route = useRoute()
const store = useStore()
const emit = defineEmits(['deleteLayer'])
const props = defineProps<{
  item: any
  position: number
}>()

type LayerType = 0 | 1 | 2 | 3 | 4 | 5 | 6

interface LayerTypeConfig {
  label: string
  icon: any
  color: string
}

const layerTypes: Record<LayerType, LayerTypeConfig> = {
  0: {
    label: 'PlanetScope',
    icon: Image,
    color: '#FF6B6B'
  },
  1: {
    label: 'Order Scene',
    icon: Package,
    color: '#4CAF50'
  },
  2: {
    label: 'Planetary Variables',
    icon: Globe,
    color: '#7ED321'
  },
  3: {
    label: 'Radar',
    icon: Radar,
    color: '#1A73E8'
  },
  4: {
    label: 'Project Data',
    icon: Folders,
    color: '#FF9900'
  },
  5: {
    label: 'Static',
    icon: Folders,
    color: '#FF9900'
  },
  6: {
    label: 'Tasking',
    icon: Satellite,
    color: '#9C27B0'
  }
}

const orgId = props.item.properties.organization_id
const layerId = `layer-${props.item.uuid}`

const map = computed(() => store.getters['geo/getMap'])
const map2 = computed(() => store.getters['geo/getMap2'])
const mapReady = computed(() => store.getters['geo/getMapReady'])
const map2Ready = computed(() => store.getters['geo/getMap2Ready'])

const isCompareMode = computed(() => store.getters['geo/getIsCompareMode'])

const elRef = ref()
const isDataLoaded = ref(false)
const isVisible = ref(props.item.properties?.visible ?? true)

// Sync local isVisible when the store value changes externally (e.g. from DetailTasking auto-enable)
watch(() => props.item.properties?.visible, (storeVisible) => {
  if (storeVisible === undefined || storeVisible === isVisible.value) return
  isVisible.value = storeVisible

  const layers = map.value?.getStyle()?.layers?.filter((layer: any) =>
    layer.metadata?.groupId === layerId
  ) || []

  layers.forEach((layer: any) => {
    map.value.setLayoutProperty(
      layer.id,
      'visibility',
      storeVisible ? 'visible' : 'none'
    )
  })
})

const isHovered = ref(false)
const isDragging = ref(false)
const isDraggedOver = ref(false)
const dropPosition = ref<'above' | 'below' | null>(null)

const layerType = computed(() => {
  const type = props.item.properties?.type as LayerType
  return layerTypes[type] || layerTypes[0]
})

const layerStatus = computed(() => {
  const status = props.item.properties?.layer?.status
  if (status === undefined || status === null) return null
  return parseInt(status)
})

const isProcessing = computed(() => {
  // Actively processing: status is positive (1-10, 100 = pending approval)
  if (layerStatus.value !== null && layerStatus.value > 0) return true
  // Data not loaded yet, layer is visible, and status is not failed/done — still initializing
  if (!isDataLoaded.value && isVisible.value && (layerStatus.value === null || layerStatus.value === 0)) return true
  return false
})

const toggleVisibility = ({ navigate = true } = {}) => {
  isVisible.value = !isVisible.value

  const layers = map.value.getStyle().layers.filter((layer: any) =>
    layer.metadata?.groupId === layerId
  )

  layers.forEach((layer: any) => {
    map.value.setLayoutProperty(
      layer.id,
      'visibility',
      isVisible.value ? 'visible' : 'none'
    )
  })

  // Update the layer in Vuex store
  const updatedLayer = {
    ...props.item,
    properties: {
      ...props.item.properties,
      visible: isVisible.value
    }
  }

  store.dispatch('layers/updateOrgLayer', {
    orgId,
    layer: updatedLayer
  })

  if (!navigate) return

  if (!isVisible.value && route.params.layer_id === props.item.uuid) {
    // Deselect layer (navigate away) when hiding the currently viewed layer
    router.push({
      name: 'OrgLayers',
      params: { org_id: orgId }
    })
  } else if (isVisible.value && !route.params.layer_id) {
    // Open layer detail when making visible and no layer page is open
    router.push({
      name: 'LayerDetail',
      params: { org_id: orgId, layer_id: props.item.uuid }
    })
  }
}

const showDeleteConfirm = ref(false)

// Inline rename state
const isEditing = ref(false)
const editingName = ref('')
const editInputRef = ref<HTMLInputElement | null>(null)

const startEditing = () => {
  isEditing.value = true
  editingName.value = props.item.properties?.name?.trim() || ''
  nextTick(() => {
    editInputRef.value?.focus()
    editInputRef.value?.select()
  })
}

const saveEdit = async () => {
  const trimmed = editingName.value.trim()
  if (!trimmed) {
    toast({ title: 'Layer name cannot be empty', variant: 'destructive' })
    return
  }

  const currentName = props.item.properties?.name?.trim() || ''
  if (trimmed === currentName) {
    cancelEdit()
    return
  }

  try {
    await store.dispatch('layers/renameOrgLayer', {
      orgId,
      layerId: props.item.uuid,
      name: trimmed
    })
    isEditing.value = false
  } catch (error) {
    toast({ title: 'Failed to rename layer', variant: 'destructive' })
  }
}

const cancelEdit = () => {
  isEditing.value = false
  editingName.value = ''
}

const handleRenameKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    e.preventDefault()
    saveEdit()
  } else if (e.key === 'Escape') {
    e.preventDefault()
    cancelEdit()
  }
}

const deleteLayer = async () => {
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  try {
    console.log('Deleting layer:', props.item.uuid, props.item.properties?.name)
    await store.dispatch('layers/deleteOrgLayer', {
      orgId,
      layerId: props.item.uuid
    })
    emit('deleteLayer', props.item.uuid)
    showDeleteConfirm.value = false
  } catch (error) {
    console.error('Error deleting layer:', error)
  }
}

const cancelDelete = () => {
  showDeleteConfirm.value = false
}

const focusOnGeometry = () => {
  const geometry = props.item?.geometry
  if (geometry) {
    const bounds = bbox(geometry)
    map.value.fitBounds(bounds, { padding: 300 })
  }
}

const moveUp = () => {
  store.dispatch('layers/moveOrgLayerUp', {
    orgId,
    layerId: props.item.uuid
  })
}

const moveDown = () => {
  store.dispatch('layers/moveOrgLayerDown', {
    orgId,
    layerId: props.item.uuid
  })
}

const navigateToLayerDetail = () => {
  if (isEditing.value) return

  // Auto-enable visibility when selecting a layer (suppress navigation since we navigate below)
  if (!isVisible.value) {
    toggleVisibility({ navigate: false })
  }

  router.push({
    name: 'LayerDetail',
    params: {
      org_id: orgId,
      layer_id: props.item.uuid
    }
  })
}

// Determine drop position based on mouse Y position relative to element
const getDropPosition = (element: HTMLElement, clientY: number): 'above' | 'below' => {
  const rect = element.getBoundingClientRect()
  const midpoint = rect.top + rect.height / 2
  return clientY < midpoint ? 'above' : 'below'
}

watchEffect((onCleanup) => {
  const currentElement = unrefElement(elRef)

  if (!currentElement)
    return

  const item = { ...props.item, id: props.item.uuid }

  const dndFunction = combine(
    draggable({
      element: currentElement,
      getInitialData: () => item,
      onDragStart: () => {
        if (isEditing.value) cancelEdit()
        isDragging.value = true
      },
      onDrop: () => {
        isDragging.value = false
      },
      onGenerateDragPreview({ nativeSetDragImage }) {
        setCustomNativeDragPreview({
          getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
          render: ({ container }) => {
            const type = props.item.properties?.type as LayerType
            const typeConfig = layerTypes[type] || layerTypes[0]
            const layerName = props.item.properties?.name?.trim() || 'Unnamed'

            return render(h(
              'div',
              {
                class: 'flex items-center gap-2 bg-background shadow-lg rounded-md text-sm font-medium px-3 py-2 border border-border',
                style: 'max-width: 200px'
              },
              [
                h(typeConfig.icon, {
                  class: 'w-4 h-4 flex-shrink-0',
                  style: { color: typeConfig.color }
                }),
                h('span', { class: 'truncate' }, layerName)
              ]
            ), container)
          },
          nativeSetDragImage,
        })
      },
    }),

    dropTargetForElements({
      element: currentElement,
      getData: ({ input }) => {
        // Calculate drop position based on mouse position
        const pos = getDropPosition(currentElement, input.clientY)
        return {
          id: item.id,
          position: pos
        }
      },
      canDrop: ({ source }) => {
        return source.data.id !== item.id
      },
      onDrag: ({ self, source }) => {
        if (source.data.id !== item.id) {
          dropPosition.value = self.data.position as 'above' | 'below'
        }
      },
      onDragEnter: ({ source }) => {
        if (source.data.id !== item.id) {
          isDraggedOver.value = true
        }
      },
      onDragLeave: () => {
        isDraggedOver.value = false
        dropPosition.value = null
      },
      onDrop: () => {
        isDraggedOver.value = false
        dropPosition.value = null
      },
      getIsSticky: () => true,
    }),
  )

  onCleanup(() => dndFunction())
})
</script>

<template>
  <div class="group">
    <div
      ref="elRef"
      class="relative w-full px-3 py-1.5 rounded cursor-pointer transition-all duration-150"
      :class="{
        'opacity-60 scale-[0.98] shadow-md ring-2 ring-primary/30 bg-primary/5': isDragging,
        'bg-primary/10': route.params.layer_id === item.uuid && !isDragging,
        'bg-muted': isDraggedOver && !isDragging
      }"
      @click="navigateToLayerDetail"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
    >
      <div class="flex justify-between items-center w-full">
        <div class="flex items-center min-w-0">
          <!-- Drag handle - visible on hover -->
          <GripVertical
            class="h-4 text-muted-foreground cursor-grab flex-shrink-0 overflow-hidden transition-all duration-150 w-0 group-hover:w-4 group-hover:mr-1"
            :class="{ 'w-4 mr-1 cursor-grabbing': isDragging }"
          />
          <div class="flex items-center min-w-0">
            <component :is="layerType.icon" class="w-4.5 h-4.5 mr-1.5 flex-shrink-0" :style="{ color: layerType.color }" />
            <Spinner v-if="isProcessing" class="w-3.5 h-3.5 mr-1 flex-shrink-0" />
            <div v-if="isEditing" class="flex items-center gap-1 min-w-0" @click.stop>
              <Input
                ref="editInputRef"
                v-model="editingName"
                class="h-6 text-sm px-1 py-0 min-w-0"
                @click.stop
                @keydown="handleRenameKeydown"
              />
              <Button size="sm" variant="ghost" class="w-6 h-6 p-0 flex-shrink-0" @click.stop="saveEdit">
                <Check class="w-3.5 h-3.5 text-green-600" />
              </Button>
            </div>
            <h3 v-else class="truncate">{{ item.properties.name?.trim() || 'Unnamed' }}</h3>
          </div>
        </div>
        <div class="flex-shrink-0" :class="{
          'opacity-0': !isHovered && !(isDataLoaded && !isProcessing && isVisible),
          'opacity-50 pointer-events-none': isHovered && (!isDataLoaded || isProcessing),
          'opacity-100': isDataLoaded && !isProcessing && (isVisible || isHovered)
        }">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" class="w-6 h-6 p-0 rounded-full">
                <Ellipsis class="w-4 h-4 text-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent class="w-48">
              <DropdownMenuGroup>
                <DropdownMenuItem v-if="item.geometry" @click="focusOnGeometry" class="cursor-pointer">
                  <ScanEye class="w-4 h-4 mr-2" />
                  <span>Fly To</span>
                </DropdownMenuItem>
                <DropdownMenuItem @click="moveUp" class="cursor-pointer">
                  <ArrowUp class="w-4 h-4 mr-2" />
                  <span>Move Up</span>
                </DropdownMenuItem>
                <DropdownMenuItem @click="moveDown" class="cursor-pointer">
                  <ArrowDown class="w-4 h-4 mr-2" />
                  <span>Move Down</span>
                </DropdownMenuItem>
                <DropdownMenuItem @click="startEditing" class="cursor-pointer">
                  <Pencil class="w-4 h-4 mr-2" />
                  <span>Rename</span>
                </DropdownMenuItem>
                <DropdownMenuItem @click="deleteLayer" class="text-red-500 cursor-pointer">
                  <Trash2 class="w-4 h-4 mr-2" />
                  <span>Delete</span>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <TooltipProvider :delayDuration="450">
            <Tooltip>
              <TooltipTrigger>
                <Button size="sm" variant="ghost" class="w-6 h-6 p-0 rounded-full" @click.stop="toggleVisibility">
                  <EyeClosed class="text-foreground w-4 h-4" v-if="!isVisible" />
                  <Eye class="text-primary w-4 h-4" v-if="isVisible" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Visibility</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div v-if="map2 && map2Ready && isCompareMode">
        <div class="mx-2">
          <span>Top</span>
        </div>
        <LayerItemMapLayer
          :map="map2"
          :item="item"
          :layerId="layerId + '-map2'"
          :position="position"
          :orgId="orgId"
          :isVisible="isVisible"
          @isLoaded="isDataLoaded = $event"
          @deleteLayer="deleteLayer"
        ></LayerItemMapLayer>
      </div>

      <div v-if="mapReady">
        <div class="mx-2">
          <span v-if="map2 && map2Ready && isCompareMode">Bottom</span>
        </div>
        <LayerItemMapLayer
          v-if="mapReady"
          :map="map"
          :item="item"
          :layerId="layerId"
          :position="position"
          :orgId="orgId"
          :isVisible="isVisible"
          @isLoaded="isDataLoaded = $event"
          @deleteLayer="deleteLayer"
        ></LayerItemMapLayer>
      </div>

      <!-- Drop indicator - simple above/below lines -->
      <div
        v-if="dropPosition"
        class="absolute pointer-events-none w-full left-0 transition-all duration-150"
        :class="{
          'top-0 border-t-4 border-primary bg-gradient-to-b from-primary/10 to-transparent h-1/2': dropPosition === 'above',
          'bottom-0 border-b-4 border-primary bg-gradient-to-t from-primary/10 to-transparent h-1/2': dropPosition === 'below',
        }"
      />
    </div>

    <!-- Delete Confirmation Dialog -->
    <Dialog v-model:open="showDeleteConfirm">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Layer</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the layer "{{ props.item.properties?.name }}"?
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" @click="cancelDelete">Cancel</Button>
          <Button variant="destructive" @click="confirmDelete">Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
